import React, { useCallback, useEffect, useState } from "react";
import API_BASE from "./apiBase";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
import Locations from "./Locations";
import DeliveryAppBranding from "./DeliveryAppBranding";

/**
 * ===========================================================================
 * ORGANISATION
 * ===========================================================================
 * Three tabs over one payload. GET /manage/group returns the group, its
 * branches and its people together, so all three tabs are one request and a
 * change on any of them refreshes all three — a new branch has to appear in
 * the People tab's branch picker immediately, or adding a branch and then a
 * driver for it fails for no visible reason.
 *
 * ROLES. driver, employee, branch manager, group manager. Only the last two
 * open anything on this website; a driver reaches the app and nothing else,
 * and an EMPLOYEE currently opens nothing at all — the role records who works
 * where rather than granting access. That is said on screen, because a role
 * that silently does nothing looks broken otherwise.
 */

const S = {
  wrap:   { padding: 0, fontFamily: "Inter, system-ui, sans-serif", color: "#111827" },
  h1:     { fontSize: 18, fontWeight: 700, margin: 0 },
  panel:  { border: "1px solid #d8dee8", borderRadius: 10, background: "#ffffff", boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
            padding: "12px 14px" },
  label:  { fontSize: 10.5, fontWeight: 800, color: "#6b7280",
            textTransform: "uppercase", letterSpacing: 0.5 },
  input:  { padding: "6px 8px", fontSize: 13, border: "1px solid #d8dee8",
            borderRadius: 8, width: "100%", boxSizing: "border-box",
            fontFamily: "inherit" },
  table:  { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:     { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #e7ebf1",
            fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4,
            color: "#6b7280", whiteSpace: "nowrap" },
  td:     { padding: "6px 8px", borderBottom: "1px solid #f2f4f7",
            verticalAlign: "middle" },
  muted:  { color: "#6b7280", fontSize: 11 },
  btn:    { background: "#ffffff", color: "#344054", border: "1px solid #d8dee8",
            borderRadius: 8, padding: "5px 9px", fontSize: 12.5, cursor: "pointer",
            fontFamily: "inherit" },
  btnGo:  { background: "#1976d2", color: "#fff", border: "1px solid #1976d2",
            borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer",
            fontFamily: "inherit", fontWeight: 600 },
  error:  { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b",
            padding: 10, borderRadius: 8, fontSize: 13 },
  note:   { background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e3a5f",
            padding: 10, borderRadius: 8, fontSize: 13 },
};

const ROLES = [
  { value: "driver",         label: "Driver",
    help: "Uses the delivery app. No access to this website." },
  { value: "employee",       label: "Employee",
    help: "On the books at a branch. Opens nothing yet — a record of who works where, not an access grant." },
  { value: "branch_manager", label: "Branch manager",
    help: "This website, limited to their own branch." },
  { value: "group_manager",  label: "Group manager",
    help: "This website, every branch, plus this screen." },
];
const roleLabel = (v) => (ROLES.find((r) => r.value === v) || {}).label || v;

const TABS = [
  { key: "organisation", label: "Organisation" },
  { key: "branches",     label: "Branches" },
  { key: "people",       label: "People" },
  { key: "locations",    label: "Locations and messaging" },
  { key: "deliveryapp",  label: "Delivery app" },
];

async function api(path, options = {}) {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  const res = await fetch(`${API_BASE}/manage${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((data && data.error && data.error.message) || `Failed (${res.status})`);
  }
  return data;
}

const when = (iso) => {
  if (!iso) return "never";
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? "never" : t.toLocaleDateString("en-GB");
};

export default function Organisation({ token }) {
  const [tab, setTab] = useState("organisation");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api("/group")); setError(null); }
    catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={S.wrap}>
      <div style={{ display: "flex", gap: 12, alignItems: "center",
                    marginBottom: 12, flexWrap: "wrap" }}>
        <h1 style={S.h1}>Configuration</h1>
        <MaskToggleButton />
        <div style={{ display: "flex", gap: 6 }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ ...S.btn,
                background: tab === t.key ? "#1976d2" : "#f3f4f6",
                color: tab === t.key ? "#fff" : "#374151",
                borderColor: tab === t.key ? "#1976d2" : "#d1d5db",
                fontSize: 13, padding: "6px 12px" }}>
              {t.label}
              {t.key === "branches" && data ? ` (${data.branches.length})` : ""}
              {t.key === "people" && data ? ` (${data.members.length})` : ""}
            </button>
          ))}
        </div>
      </div>

      {error && <div style={{ ...S.error, marginBottom: 12 }}>{error}</div>}
      {!data && !error && tab !== "locations" && <div style={{ ...S.panel, ...S.muted }}>Loading…</div>}

      {data && tab === "organisation" && <OrgTab data={data} />}
      {data && tab === "branches" && <BranchesTab data={data} reload={load} />}
      {data && tab === "people" && <PeopleTab data={data} reload={load} />}
      {/* Locations manages its own branch list and loading state, and doesn't
          need the /manage/group payload the other three tabs share — so it
          isn't gated on `data` the way they are. */}
      {tab === "locations" && <Locations token={token} />}
      {tab === "deliveryapp" && <DeliveryAppBranding embedded={false} />}
    </div>
  );
}

function OrgTab({ data }) {
  const masked = useMaskNames();
  const active = data.branches.filter((b) => b.is_active).length;
  const activePeople = data.members.filter((m) => m.is_active).length;
  const byRole = ROLES.map((r) => ({
    ...r,
    n: data.members.filter((m) => m.role === r.value && m.is_active).length,
  }));
  const unallocated = data.members.filter((m) => m.is_active && m.branch_count === 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 720 }}>
      <div style={S.panel}>
        <div style={S.label}>Organisation</div>
        <div style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>
          {displayName(data.group.name, masked)}
        </div>
        <div style={S.muted}>Set up {when(data.group.created_at)}</div>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ ...S.panel, flex: 1 }}>
          <div style={S.label}>Branches</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{active}</div>
          {active !== data.branches.length && (
            <div style={S.muted}>{data.branches.length - active} inactive</div>
          )}
        </div>
        <div style={{ ...S.panel, flex: 1 }}>
          <div style={S.label}>People</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{activePeople}</div>
          {activePeople !== data.members.length && (
            <div style={S.muted}>{data.members.length - activePeople} inactive</div>
          )}
        </div>
      </div>

      <div style={S.panel}>
        <div style={{ ...S.label, marginBottom: 6 }}>Who does what</div>
        {byRole.map((r) => (
          <div key={r.value} style={{ display: "flex", justifyContent: "space-between",
                                      padding: "4px 0", borderBottom: "1px solid #f3f4f6" }}>
            <span title={r.help}>{r.label}</span>
            <span style={{ fontWeight: 600 }}>{r.n}</span>
          </div>
        ))}
      </div>

      {/* Somebody in the group with no branch can sign in and see an empty
          screen. That looks like a broken account, so it is surfaced here
          rather than left to be noticed. */}
      {unallocated.length > 0 && (
        <div style={S.note}>
          {unallocated.length} {unallocated.length === 1 ? "person has" : "people have"} no
          branch allocated — they can sign in but will see nothing. Fix that on
          the People tab: {unallocated.map((m) => displayName(m.full_name, masked)).join(", ")}.
        </div>
      )}
    </div>
  );
}

function BranchesTab({ data, reload }) {
  const masked = useMaskNames();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const add = async () => {
    setBusy(true); setError(null);
    try {
      await api("/branches", { method: "POST", body: JSON.stringify({ name }) });
      setName(""); reload();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const setActive = async (b, is_active) => {
    setError(null);
    try {
      await api(`/branches/${b.id}`, {
        method: "PATCH", body: JSON.stringify({ is_active }),
      });
      reload();
    } catch (e) { setError(e.message); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {error && <div style={S.error}>{error}</div>}

      <div style={{ ...S.panel, display: "flex", gap: 8, alignItems: "flex-end",
                    maxWidth: 520 }}>
        <div style={{ flex: 1 }}>
          <div style={S.label}>New branch</div>
          <input style={{ ...S.input, marginTop: 4 }} value={name}
            placeholder="Frome depot"
            onChange={(e) => setName(e.target.value)} />
        </div>
        <button style={S.btnGo} disabled={busy || !name.trim()} onClick={add}>
          {busy ? "Adding…" : "Add branch"}
        </button>
      </div>

      <div style={S.panel}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Branch</th>
              <th style={S.th}>People</th>
              <th style={S.th}>Parcels waiting</th>
              <th style={S.th}>Added</th>
              <th style={S.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.branches.map((b) => (
              <tr key={b.id} style={b.is_active ? {} : { background: "#f9fafb" }}>
                <td style={S.td}>
                  <b>{displayName(b.name, masked)}</b>
                </td>
                <td style={S.td}>{b.user_count}</td>
                <td style={S.td}>{b.pending_count}</td>
                <td style={{ ...S.td, ...S.muted }}>{when(b.created_at)}</td>
                <td style={S.td}>
                  {/* Branches are deactivated, never deleted — their parcels,
                      rounds and logs stay attached to them, and removing one
                      would orphan years of history. */}
                  <button style={S.btn} onClick={() => setActive(b, !b.is_active)}>
                    {b.is_active ? "Active — deactivate" : "Inactive — reactivate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PeopleTab({ data, reload }) {
  const masked = useMaskNames();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [newPassword, setNewPassword] = useState(null);

  const patch = async (m, body) => {
    setError(null);
    try {
      await api(`/members/${m.id}`, { method: "PATCH", body: JSON.stringify(body) });
      reload();
    } catch (e) { setError(e.message); }
  };

  // A full set, not a delta — the server works out what changed and refuses
  // any removal that would strand work.
  const setBranches = async (m, accountIds) => {
    setError(null);
    try {
      await api(`/members/${m.id}/branches`, {
        method: "PUT",
        body: JSON.stringify({ account_ids: accountIds }),
      });
      reload();
    } catch (e) {
      setError(e.message);
      // Rethrown so the editor knows not to close: the message explains what
      // has to be dealt with first, and closing would hide the selection the
      // user has to correct.
      throw e;
    }
  };

  const resetPassword = async (m) => {
    setError(null);
    try {
      const r = await api(`/members/${m.id}/reset_password`, { method: "POST" });
      setNewPassword({ name: m.full_name, password: r.temporary_password });
    } catch (e) { setError(e.message); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {error && <div style={S.error}>{error}</div>}

      {/* Shown once and never recoverable, so it stays on screen until it is
          dismissed rather than fading after a few seconds. */}
      {newPassword && (
        <div style={S.note}>
          <b>New password for {displayName(newPassword.name, masked)}:</b>{" "}
          <code style={{ fontSize: 15, letterSpacing: 1 }}>{newPassword.password}</code>
          <div style={S.muted}>
            Write this down now — it cannot be shown again, only reset.
          </div>
          <button style={{ ...S.btn, marginTop: 6 }}
                  onClick={() => setNewPassword(null)}>Done</button>
        </div>
      )}

      {adding ? (
        <AddPerson branches={data.branches.filter((b) => b.is_active)}
          onCancel={() => setAdding(false)}
          onCreated={(pw, name) => {
            setAdding(false);
            setNewPassword({ name, password: pw });
            reload();
          }} />
      ) : (
        <div>
          <button style={S.btnGo} onClick={() => setAdding(true)}>Add a person</button>
        </div>
      )}

      <div style={S.panel}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Name</th>
              <th style={S.th}>Role</th>
              <th style={S.th}>Branches</th>
              <th style={S.th}>Last signed in</th>
              <th style={S.th}>Status</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.id} style={m.is_active ? {} : { background: "#f9fafb" }}>
                <td style={S.td}>
                  <b>{displayName(m.full_name, masked)}</b>
                  {m.email && <div style={S.muted}>{m.email}</div>}
                </td>
                <td style={S.td}>
                  <select style={{ ...S.input, width: "auto", fontSize: 12.5 }}
                    value={m.role}
                    onChange={(e) => patch(m, { role: e.target.value })}>
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value} title={r.help}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={S.td}>
                  <BranchCell
                    member={m}
                    branches={data.branches}
                    masked={masked}
                    onSave={(ids) => setBranches(m, ids)} />
                </td>
                <td style={{ ...S.td, ...S.muted }}>{when(m.last_login_at)}</td>
                <td style={S.td}>
                  <button style={S.btn}
                    onClick={() => patch(m, { is_active: !m.is_active })}>
                    {m.is_active ? "Active — deactivate" : "Inactive — reactivate"}
                  </button>
                </td>
                <td style={S.td}>
                  <button style={S.btn} onClick={() => resetPassword(m)}>
                    Reset password
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ ...S.muted, marginTop: 8 }}>
          People are deactivated rather than deleted — their deliveries, rounds
          and clockings stay attached to them.
        </div>
      </div>
    </div>
  );
}

/**
 * Which branches one person works at.
 *
 * READ-ONLY UNTIL ASKED. The People table is mostly scanned, not edited, and a
 * row of live checkboxes in every cell turns a list of who works where into a
 * wall of controls. So the cell shows the names, and "Change" opens the picker
 * in place.
 *
 * NOT SAVED PER TICK. Each box is a separate membership change with real
 * consequences - a removal creates a tote, a removal can be refused because a
 * round is open - so a half-finished selection must not be sent. The set goes
 * in one request when Save is pressed, which is also what makes "All branches"
 * a single decision rather than one write per branch.
 *
 * "ALL BRANCHES" IS A SNAPSHOT, NOT A STANDING RULE. It ticks every active
 * branch that exists right now. A branch added next month will NOT pick this
 * person up automatically, because nothing in the data says "everyone" - it
 * says which branches, by id. The label says as much rather than implying a
 * rule that is not there.
 */
function BranchCell({ member, branches, masked, onSave }) {
  const [editing, setEditing] = useState(false);
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);

  // Inactive branches are not offered, but one the member ALREADY holds stays
  // in the list - otherwise saving would silently drop it, and the person
  // editing would have no way of knowing they had just removed something.
  const options = branches.filter(
    (b) => b.is_active || member.branch_ids.includes(b.id),
  );

  const open = () => {
    setChosen(member.branch_ids || []);
    setEditing(true);
  };

  const toggle = (id) => setChosen(
    (c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]),
  );

  const save = async () => {
    setBusy(true);
    try {
      await onSave(chosen);
      setEditing(false);
    } catch {
      // The table above shows the reason. Leave the picker open on the
      // selection that caused it.
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div>
        {member.branch_count === 0
          ? <span style={{ color: "#b45309", fontWeight: 600 }}>None yet</span>
          : displayName(member.branch_names, masked)}
        <button style={{ ...S.btn, marginLeft: 8, padding: "2px 8px", fontSize: 11.5 }}
          onClick={open}>
          Change
        </button>
      </div>
    );
  }

  const allActive = options.filter((b) => b.is_active).map((b) => b.id);
  const hasAll = allActive.length > 0 && allActive.every((id) => chosen.includes(id));

  return (
    <div style={{ border: "1px solid #1976d2", borderRadius: 6, padding: 8,
                  background: "#eff6ff", minWidth: 220 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 6 }}>
        {options.map((b) => (
          <label key={b.id} style={{ fontSize: 12.5, display: "flex",
                                     alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={chosen.includes(b.id)}
              onChange={() => toggle(b.id)} />
            {displayName(b.name, masked)}
            {!b.is_active && <span style={S.muted}>(inactive)</span>}
          </label>
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <button style={{ ...S.btn, padding: "3px 9px", fontSize: 11.5 }}
          title="Tick every branch that exists today. New branches added later will not include this person automatically."
          onClick={() => setChosen(hasAll ? [] : allActive)}>
          {hasAll ? "Clear all" : "All branches"}
        </button>
        <div style={{ flex: 1 }} />
        <button style={{ ...S.btn, padding: "3px 9px", fontSize: 11.5 }}
          disabled={busy} onClick={() => setEditing(false)}>
          Cancel
        </button>
        <button style={{ ...S.btnGo, padding: "3px 11px", fontSize: 11.5 }}
          disabled={busy} onClick={save}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>

      {chosen.length === 0 && (
        <div style={{ ...S.muted, color: "#b45309", marginTop: 6 }}>
          With no branches they can sign in but see nothing.
        </div>
      )}
    </div>
  );
}

function AddPerson({ branches, onCancel, onCreated }) {
  const [form, setForm] = useState({ role: "driver", branch_ids: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleBranch = (id) => setForm((f) => ({
    ...f,
    branch_ids: f.branch_ids.includes(id)
      ? f.branch_ids.filter((x) => x !== id)
      : [...f.branch_ids, id],
  }));

  const role = ROLES.find((r) => r.value === form.role);
  const needsBranch = form.role !== "group_manager";

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api("/members", { method: "POST", body: JSON.stringify(form) });
      onCreated(r.temporary_password, r.member.full_name);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={S.panel}>
      {error && <div style={{ ...S.error, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        <div>
          <div style={S.label}>Name</div>
          <input style={{ ...S.input, marginTop: 4 }} value={form.full_name || ""}
            onChange={(e) => set("full_name", e.target.value)} />
        </div>
        <div>
          <div style={S.label}>Username — how they sign in</div>
          <input style={{ ...S.input, marginTop: 4 }} value={form.username || ""}
            onChange={(e) => set("username", e.target.value)} />
        </div>
        <div>
          <div style={S.label}>Email (optional)</div>
          <input style={{ ...S.input, marginTop: 4 }} value={form.email || ""}
            onChange={(e) => set("email", e.target.value)} />
        </div>
        <div>
          <div style={S.label}>Role</div>
          <select style={{ ...S.input, marginTop: 4 }} value={form.role}
            onChange={(e) => set("role", e.target.value)}>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <div style={{ ...S.muted, marginTop: 3 }}>{role && role.help}</div>
        </div>
      </div>

      {needsBranch && (
        <div style={{ marginTop: 10 }}>
          <div style={S.label}>Branches</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
            {branches.length === 0 ? (
              <span style={S.muted}>Add a branch first — on the Branches tab.</span>
            ) : branches.map((b) => (
              <label key={b.id} style={{ fontSize: 13, display: "flex",
                                         alignItems: "center", gap: 5 }}>
                <input type="checkbox"
                  checked={form.branch_ids.includes(b.id)}
                  onChange={() => toggleBranch(b.id)} />
                {b.name}
              </label>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
        <button style={S.btnGo}
          disabled={busy || !form.full_name || !form.username
            || (needsBranch && form.branch_ids.length === 0)}
          onClick={submit}>
          {busy ? "Creating…" : "Create"}
        </button>
        <button style={S.btn} disabled={busy} onClick={onCancel}>Cancel</button>
        {/* Set here rather than typed by the manager: a manager inventing
            passwords for a fleet uses the same one every time. */}
        <span style={S.muted}>A password is generated and shown once.</span>
      </div>
    </div>
  );
}
